import { orderBy } from "es-toolkit/compat";
import { useMemo } from "react";
import type { SortingType } from "../component-props";

/**
 * A hook that sorts a list of data based on the provided configuration. Does
 * not mutate the data but returns a new copy. This is a "slice and dice" hook.
 *
 * When using with other "slice and dice" hooks, make sure to run this after
 * filtering of any kind but before pagination.
 *
 * @param data - data to sort
 * @param config - configuration for the sorting
 * @returns sorted data and the sorting configuration
 */
export const useSort = <TData>(
  data: TData[] = [],
  config: SortingType,
  sortPredicate?: (items: TData[], config: SortingType) => TData[]
) => {
  const sortedData = useMemo(
    () =>
      config.sortDirection !== undefined
        ? typeof sortPredicate === "function"
          ? sortPredicate(data, config)
          : orderBy(data, [config.sortBy], [config.sortDirection])
        : data,
    [data, config.sortBy, config.sortDirection, sortPredicate]
  );

  return {
    /** sorted data. non-mutated copy. */
    data: sortedData,
    /** sorting configuration */
    config,
  };
};
